import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import hosys, { setMemToken, getMemToken } from '@/api/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    try {
      const userData = await hosys.auth.me();
      setUser(userData);
      setIsAuthenticated(true);
    } catch (err) {
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const userData = await hosys.auth.login(email, password);
    setUser(userData);
    setIsAuthenticated(true);
    return userData;
  };

  const register = async (data) => {
    const API_URL = process.env.REACT_APP_BACKEND_URL || '';
    const token = getMemToken();
    const response = await fetch(`${API_URL}/api/auth/register`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
      },
      credentials: 'include',
      body: JSON.stringify(data),
    });
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Registration failed' }));
      throw new Error(error.detail || 'Registration failed');
    }
    
    const result = await response.json();
    if (result.token) setMemToken(result.token);
    setUser(result.user);
    setIsAuthenticated(true);
    return result;
  };

  const loginWithGoogle = () => {
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const exchangeSession = async (sessionId) => {
    const API_URL = process.env.REACT_APP_BACKEND_URL || '';
    const response = await fetch(`${API_URL}/api/auth/session`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ session_id: sessionId }),
    });
    
    if (!response.ok) {
      throw new Error('Session exchange failed');
    }
    
    const result = await response.json();
    if (result.token) setMemToken(result.token);
    setUser(result.user);
    setIsAuthenticated(true);
    return result;
  };

  const logout = async () => {
    try {
      await hosys.auth.logout();
    } catch (err) {
      // Ignore errors
    }
    setUser(null);
    setIsAuthenticated(false);
  };

  const updateUser = async (data) => {
    const API_URL = process.env.REACT_APP_BACKEND_URL || '';
    const response = await fetch(`${API_URL}/api/auth/me`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data),
    });
    
    if (!response.ok) {
      throw new Error('Update failed');
    }
    
    const updated = await response.json();
    setUser(updated);
    return updated;
  };

  // Aliases for Base44 compatibility
  const isLoadingAuth = loading;
  const isLoadingPublicSettings = false;
  const authError = error;
  const appPublicSettings = null;

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      isLoadingAuth,
      isLoadingPublicSettings,
      error,
      authError,
      appPublicSettings,
      login,
      register,
      loginWithGoogle,
      exchangeSession,
      logout,
      updateUser,
      checkAuth,
      isAuthenticated,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}

export default AuthContext;
